import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { requestFormById } from 'api/operations/formService';
import { saveIMQCategoryAnswers } from 'api/operations/electionService';
import { getRootStoreInstance } from 'store/rootStore';
import { SubscriberImqModule } from 'store/modules/subscriber/subscriber-imq/subscriberImqModule';
import { Primitive } from 'types/general';
import { HealthQuestionnaireDomainParams } from './types';

export interface MedicalCategoryPageDomainParams extends HealthQuestionnaireDomainParams {
  category: string;
}

export default abstract class IMedicalCategoryPage {
  private readonly _rootStore = getRootStoreInstance();
  protected readonly _domainParams: MedicalCategoryPageDomainParams;
  public readonly subscriberImqModule: SubscriberImqModule;

  public dynamicFormDelegate = runInAction(
    () =>
      new DynamicFormDelegate({
        currentUserRoles: this._rootStore.userStore.userRoles,
        fetchForm: async (formId: string): Promise<FormObjectFromJSON> => requestFormById(formId),
        onSubmit: async (): Promise<void> => {},
        disableResetAfterSubmit: true,
      }),
  );

  private _formId: string | null = null;
  private _categoryAnswers: Record<string, Primitive> = {};
  private _nextCategoryName: string | null = null;
  private _isInitialDataLoading = true;

  public get domainParams(): MedicalCategoryPageDomainParams {
    return this._domainParams;
  }

  public get isActionLoading(): boolean {
    return this.dynamicFormDelegate.isLoading;
  }

  public get isFormValid(): boolean {
    return this.dynamicFormDelegate.isFormValid;
  }

  public get formId(): string | null {
    return this._formId;
  }

  public get categoryName(): string {
    return this._domainParams.category;
  }

  public get nextCategoryName(): string | null {
    return this._nextCategoryName;
  }

  public abstract get isInitialLoading(): boolean;

  public abstract returnBack(): void;

  protected abstract navigateToCategory(categoryName: string): void;

  protected get isPageContentLoading(): boolean {
    return this._isInitialDataLoading;
  }

  protected setIsInitialDataLoading(isLoading: boolean): void {
    this._isInitialDataLoading = isLoading;
  }

  public async getInitialData(): Promise<void> {
    this.setIsInitialDataLoading(true);
    await Promise.all([this.subscriberImqModule.getSubscriber(), this.subscriberImqModule.getRequiredSubInfo()]);
    runInAction(() => {
      this.subscriberImqModule.buildImqData();
      this._setCategoryData();
    });
    await runInAction(async () => {
      await this._buildDynamicForm();
      this.setIsInitialDataLoading(false);
    });
  }

  public async saveAndReturnToList(): Promise<void> {
    const saved = await this._saveCategoryAnswers();

    runInAction(() => {
      if (saved) {
        this.returnBack();
      }
    });
  }

  public async saveAndGoToNextForm(): Promise<void> {
    const saved = await this._saveCategoryAnswers();
    runInAction(() => {
      const nextCategory = this.nextCategoryName;

      if (saved && nextCategory) {
        this.navigateToCategory(nextCategory);
      } else {
        this.returnBack();
      }
    });
  }

  private _setCategoryData(): void {
    const categoryName = this._domainParams.category;
    const categories = this.subscriberImqModule.requiredSubInfo?.imq.categories ?? [];
    const currentIndex = categories.findIndex((category) => category.name === categoryName);
    const categoryConfig = currentIndex === -1 ? undefined : categories[currentIndex];
    const categoryAnswers = this.subscriberImqModule.subscriber?.imqAnswers?.categories.find(
      (category) => category.name === categoryName,
    );

    this._formId = categoryConfig?.formId ?? null;
    this._categoryAnswers = categoryAnswers?.answers ?? {};
    this._nextCategoryName =
      currentIndex === -1 || currentIndex >= categories.length - 1 ? null : categories[currentIndex + 1].name;
  }

  private async _buildDynamicForm(): Promise<void> {
    if (!this._formId) {
      throw new Error(`No form found for category: ${this._domainParams.category}`);
    }

    await this.dynamicFormDelegate.buildForm(this._formId, this._categoryAnswers as Record<string, string>);
  }

  private async _saveCategoryAnswers(): Promise<boolean> {
    if (!this.isFormValid) {
      return false;
    }

    this.dynamicFormDelegate.formController?.setLoading(true);

    await saveIMQCategoryAnswers(
      this.domainParams.subscriberId,
      this._domainParams.category,
      this.dynamicFormDelegate.formAnswers,
    );

    runInAction(() => {
      this.dynamicFormDelegate.formController?.setLoading(false);
    });

    return true;
  }

  constructor(domainParams: MedicalCategoryPageDomainParams) {
    this._domainParams = domainParams;
    this.subscriberImqModule = new SubscriberImqModule({ subscriberId: domainParams.subscriberId });

    makeObservable<
      IMedicalCategoryPage,
      | '_formId'
      | '_categoryAnswers'
      | '_nextCategoryName'
      | '_isInitialDataLoading'
      | 'isPageContentLoading'
      | 'setIsInitialDataLoading'
      | '_setCategoryData'
      | '_buildDynamicForm'
      | '_saveCategoryAnswers'
    >(this, {
      _formId: observable,
      _categoryAnswers: observable,
      _nextCategoryName: observable,
      _isInitialDataLoading: observable,
      domainParams: computed,
      isActionLoading: computed,
      isFormValid: computed,
      formId: computed,
      categoryName: computed,
      nextCategoryName: computed,
      isPageContentLoading: computed,
      setIsInitialDataLoading: action.bound,
      getInitialData: action.bound,
      saveAndReturnToList: action.bound,
      saveAndGoToNextForm: action.bound,
      _setCategoryData: action.bound,
      _buildDynamicForm: action.bound,
      _saveCategoryAnswers: action.bound,
    });
  }
}
